import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { Clock } from "lucide-react";
import BrandMark from "./BrandMark";
import PreviewValue from "./PreviewValue";
import mealPlanBg from "../../../assets/svg/my brand/meal plan-bg.svg";
import { MealSpecimen, mealPlanSample, previewSample } from "../data/brandMock";
import {
  PlanDocumentSpecimen,
  guidelinesSpecimen,
  policySpecimen,
} from "../data/planDocumentSpecimen";
import {
  CONTENT_HEIGHT,
  CONTENT_WIDTH,
  MARGIN_BOTTOM,
  MARGIN_X,
  PAGE_HEIGHT,
  PAGE_WIDTH,
  SHEET_PADDING,
} from "../previewPage";
import { BrandDetails } from "../types/brand.types";
import { senderName } from "../previewFields";

/** Column in the strip under the client's name. */
const PlanFact: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="min-w-0">
    <div className="text-[7px] text-ink-subtle">{label}</div>
    <div className="mt-0.5 text-[9px] text-[#111C2D] truncate">{value}</div>
  </div>
);

/**
 * One sheet of the document, at the page's own size. The number is the
 * counter the PDF prints in the bottom margin — two digits, and absent on
 * the cover, which is full bleed and carries no margin box.
 */
const Sheet: React.FC<{
  number?: number;
  children: React.ReactNode;
}> = ({ number, children }) => (
  <div
    style={{ width: PAGE_WIDTH, height: PAGE_HEIGHT }}
    className="relative flex-shrink-0 overflow-hidden bg-white"
  >
    {children}
    {number !== undefined && (
      <span
        style={{ right: MARGIN_X, bottom: MARGIN_BOTTOM - 12 }}
        className="absolute text-[8px] font-medium text-[#96A3A8]"
      >
        Page {String(number).padStart(2, "0")}
      </span>
    )}
  </div>
);

/** The cover: the practice's mark, and who the plan is for. */
const CoverSheet: React.FC<{ details: BrandDetails }> = ({ details }) => (
  <Sheet>
    {/* The plan template's artwork, full bleed behind the content. `cover`
        rather than a stretch: the page and the artboard are within a couple
        of percent of each other, and skewing the geometry shows. */}
    <img
      src={mealPlanBg}
      alt=""
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 h-full w-full object-cover"
    />

    <div className="relative flex h-full flex-col justify-center px-14">
      {/* Same box as the invoice's — the brand's logo reads as one
          consistent size across every document, not rescaled per page. */}
      <BrandMark
        details={details}
        size={40}
        width={100}
        squareSize={{ width: 52, height: 52 }}
      />

      <h3 className="mt-8 text-[25px] font-semibold leading-tight text-[#111C2D]">
        {mealPlanSample.clientName}
      </h3>
      <div className="mt-1.5 text-[8px] text-ink-subtle">
        Plan ID :{" "}
        <span className="font-semibold text-[#00899B]">
          {mealPlanSample.planId}
        </span>
      </div>

      {/* "Prepared by" holds the longest value of the three, so it takes the
          wider track rather than an equal third. */}
      <div className="mt-6 grid grid-cols-[1fr_1fr_2fr] gap-4 rounded-[4px] bg-[#F5F7F8] px-2.5 py-2">
        <PlanFact label="Created on" value={mealPlanSample.createdOn} />
        <PlanFact label="Goal" value={mealPlanSample.goal} />
        <PlanFact
          label="Prepared by"
          value={
            <PreviewValue
              value={senderName(details)}
              sample={previewSample.fullName}
              className="text-[#111C2D]"
            />
          }
        />
      </div>
    </div>
  </Sheet>
);

/**
 * One meal, on its own sheet — which is what the PDF does and what a reader
 * asked for: a single meal can be put in front of a client on one sheet.
 * Every colour and rule here is the template's; see `meal_plan.html`.
 */
const MealSheet: React.FC<{ meal: MealSpecimen; number: number }> = ({
  meal,
  number,
}) => (
  <Sheet number={number}>
    <div style={{ padding: SHEET_PADDING }}>
      <div className="mb-[17px] flex items-start justify-between">
        <div>
          {/* Nowrap: the row is flex, and without it the title shrinks and
              wraps while the time pill keeps its width. */}
          <h4 className="whitespace-nowrap text-[18px] font-medium leading-tight tracking-[-0.3px] text-[#0D3D44]">
            {meal.label}
          </h4>
          <div className="mt-[3px] text-[10px] text-[#5A6B72]">
            {meal.options.length} meal option
            {meal.options.length === 1 ? "" : "s"}
          </div>
        </div>
        <div className="flex flex-shrink-0 items-center gap-[4px] whitespace-nowrap rounded-full bg-[#D7F9FD] px-[11px] py-[5px] text-[10px] text-[#00424B]">
          <Clock aria-hidden="true" className="h-[10px] w-[10px]" />
          {meal.time}
        </div>
      </div>

      {meal.options.map((option, index) => (
        <div
          key={index}
          className="mb-[14px] overflow-hidden rounded-[6px] border border-[#00BACB]"
        >
          <div className="flex justify-between bg-[#17B6C9] px-[12px] py-[8px] text-white">
            <span className="text-[10px] font-semibold uppercase tracking-[0.3px]">
              Option {index + 1}
            </span>
            <span className="text-[9px] font-semibold">Quantity</span>
          </div>

          {option.items.map((item, row) => (
            <div
              key={row}
              className="flex justify-between gap-3 border-b border-[#00BACB] px-[12px] py-[9px] text-[10.5px]"
            >
              <span className="font-medium text-[#16232A]">{item.food}</span>
              <span className="text-right font-medium text-[#5A6B72]">
                {item.amount}
              </span>
            </div>
          ))}

          {/* The four figures are carried by their accent colours, so the row
              stays at regular weight rather than competing with the food
              names above it. */}
          <div className="flex justify-between bg-[#F3FBFC] px-[12px] py-[8px] text-[10px] text-[#5A6B72]">
            <span>
              Calories - <span className="text-[#E8912F]">{option.kcal}</span>
            </span>
            <span>
              Protein - <span className="text-[#17B6C9]">{option.protein}g</span>
            </span>
            <span>
              Carb - <span className="text-[#E8912F]">{option.carbs}g</span>
            </span>
            <span>
              Fats - <span className="text-[#E0567A]">{option.fat}g</span>
            </span>
          </div>
        </div>
      ))}
    </div>
  </Sheet>
);

/**
 * One thing that moves to a sheet whole: the document's own name over its
 * first sheet, a section heading, or a single numbered line. A section is
 * split into its lines so a long one runs over the foot of a sheet onto the
 * next, the way it does in print.
 */
interface Unit {
  key: string;
  node: React.ReactNode;
  /** A heading takes the line under it with it rather than standing alone
   *  at the foot of a sheet — the one break this layout must not make. */
  heading?: boolean;
}

const unitsOf = (specimen: PlanDocumentSpecimen): Unit[] => {
  const units: Unit[] = [
    {
      key: "title",
      heading: true,
      node: (
        <div className="mb-[8px] text-[8px] font-semibold uppercase tracking-[1px] text-[#0A8A9C]">
          {specimen.title}
        </div>
      ),
    },
  ];

  specimen.sections.forEach((section, index) => {
    units.push({
      key: `heading-${index}`,
      heading: true,
      node: (
        <h4 className="mb-[11px] text-[17px] font-medium leading-[1.25] tracking-[-0.3px] text-[#0A8A9C]">
          {section.heading}
        </h4>
      ),
    });

    section.items.forEach((item, row) => {
      // The space between sections hangs under the last line of one rather
      // than over the next heading: a margin above a heading that lands at
      // the top of a sheet would push it off the sheet's own margin.
      const last = row === section.items.length - 1;
      units.push({
        key: `item-${index}-${row}`,
        node: (
          <div
            className={`flex gap-[6px] text-[9.5px] leading-[1.6] text-[#16232A] ${
              last ? "mb-[14px]" : "mb-[5px]"
            }`}
          >
            <span className="w-[14px] flex-shrink-0 text-right">
              {section.ordered ? `${row + 1}.` : "•"}
            </span>
            <p className="text-justify">
              <span className="font-semibold">{item.term}:</span> {item.text}
            </p>
          </div>
        ),
      });
    });
  });

  return units;
};

const outerHeight = (element: Element): number => {
  const style = getComputedStyle(element);
  return (
    element.getBoundingClientRect().height +
    parseFloat(style.marginTop) +
    parseFloat(style.marginBottom)
  );
};

/**
 * The units dealt onto sheets, in order, each sheet taking what fits. A unit
 * taller than a sheet gets one of its own and is clipped at its foot, the
 * way a printer would.
 */
const paginate = (units: Unit[], heights: number[]): Unit[][] => {
  const sheets: Unit[][] = [[]];
  let used = 0;

  units.forEach((unit, index) => {
    const sheet = sheets[sheets.length - 1];
    const height = heights[index] ?? 0;
    // A heading is measured together with the line under it, so the pair
    // move to the next sheet rather than splitting across the break.
    const needed = unit.heading ? height + (heights[index + 1] ?? 0) : height;

    if (sheet.length > 0 && used + needed > CONTENT_HEIGHT) {
      sheets.push([unit]);
      used = height;
    } else {
      sheet.push(unit);
      used += height;
    }
  });

  return sheets;
};

/**
 * Where a document's sheets are worked out: it is drawn once, out of sight,
 * at the width the words run at, and each piece's height is read off the
 * page it is actually drawn on. Re-done whenever the measuring box re-flows
 * — the web font swapping in after first paint, mostly, which is what makes
 * a split taken before it wrong.
 */
const useSheets = (specimen: PlanDocumentSpecimen) => {
  const units = useMemo(() => unitsOf(specimen), [specimen]);
  const measureRef = useRef<HTMLDivElement>(null);
  const [sheets, setSheets] = useState<Unit[][]>([]);

  useLayoutEffect(() => {
    const box = measureRef.current;
    if (!box) return;

    const split = () =>
      setSheets(paginate(units, Array.from(box.children).map(outerHeight)));

    split();
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(split);
    observer?.observe(box);
    return () => observer?.disconnect();
  }, [units]);

  /** The whole document, out of sight, at the sheet's own measure. */
  const ruler = (
    <div
      ref={measureRef}
      aria-hidden="true"
      style={{ width: CONTENT_WIDTH }}
      className="pointer-events-none invisible absolute left-0 top-0"
    >
      {units.map((unit) => (
        <React.Fragment key={unit.key}>{unit.node}</React.Fragment>
      ))}
    </div>
  );

  return { sheets, ruler };
};

const DocumentSheet: React.FC<{ units: Unit[]; number: number }> = ({
  units,
  number,
}) => (
  <Sheet number={number}>
    <div style={{ padding: SHEET_PADDING }}>
      {units.map((unit) => (
        <React.Fragment key={unit.key}>{unit.node}</React.Fragment>
      ))}
    </div>
  </Sheet>
);

interface MealPlanPreviewProps {
  details: BrandDetails;
}

/**
 * A generated meal plan, whole: the cover, a sheet for each meal, and the
 * practice's Guidelines and Policy as the closing sheets — the document a
 * client is sent, which is what the panel beside the form is there to show.
 *
 * The dietitian's branding is the live form's; the client, the plan code and
 * the meals are specimen values standing in for whatever plan the document
 * is generated from. The closing two are the Naurist defaults — a practice
 * that has written its own reads those back in Guidelines & policy, and the
 * PDF prints whichever is stored.
 *
 * Every meal starts a new sheet because the PDF does: a reader can put one
 * meal in front of a client on one sheet. The closing documents run to as
 * many sheets as their text takes, worked out by measurement — see
 * `useSheets`.
 */
const MealPlanPreview: React.FC<MealPlanPreviewProps> = ({ details }) => {
  const guidelines = useSheets(guidelinesSpecimen);
  const policy = useSheets(policySpecimen);

  // The counter the PDF prints, which counts the cover as page 1 without
  // printing it there.
  const firstMeal = 2;
  const firstGuideline = firstMeal + mealPlanSample.meals.length;
  const firstPolicy = firstGuideline + guidelines.sheets.length;

  return (
    // The sheets in a stack, with the ground showing between them: a document
    // of seven pages has to read as seven sheets rather than one long one.
    <div
      style={{ width: PAGE_WIDTH }}
      className="relative flex flex-col gap-2 bg-line"
    >
      <CoverSheet details={details} />

      {mealPlanSample.meals.map((meal, index) => (
        <MealSheet key={meal.label} meal={meal} number={firstMeal + index} />
      ))}

      {guidelines.sheets.map((units, index) => (
        <DocumentSheet
          key={`guidelines-${index}`}
          units={units}
          number={firstGuideline + index}
        />
      ))}

      {policy.sheets.map((units, index) => (
        <DocumentSheet
          key={`policy-${index}`}
          units={units}
          number={firstPolicy + index}
        />
      ))}

      {guidelines.ruler}
      {policy.ruler}
    </div>
  );
};

export default MealPlanPreview;
